/** Studio subscription catalog — dollars in TTD cents. Customer language is dollars, not credits. */

export type StudioPlanInterval = "month" | "year";

export type StudioPlanCatalogEntry = {
  slug: "core" | "plus" | "pro";
  name: string;
  /** Face value added to the account each month (what they get). */
  faceMonthlyCents: number;
  /** Off the monthly charge. They still receive faceMonthlyCents. */
  monthlyDiscountPercent: number;
  /** Off the prepaid yearly charge. Credits still land monthly. */
  annualDiscountPercent: number;
  sortOrder: number;
};

export const STUDIO_PLAN_CATALOG: readonly StudioPlanCatalogEntry[] = [
  {
    slug: "core",
    name: "Core",
    faceMonthlyCents: 25_000,
    monthlyDiscountPercent: 0,
    annualDiscountPercent: 3,
    sortOrder: 0,
  },
  {
    slug: "plus",
    name: "Plus",
    faceMonthlyCents: 50_000,
    monthlyDiscountPercent: 0,
    annualDiscountPercent: 8,
    sortOrder: 1,
  },
  {
    slug: "pro",
    name: "Pro",
    faceMonthlyCents: 200_000,
    monthlyDiscountPercent: 0,
    annualDiscountPercent: 12,
    sortOrder: 2,
  },
] as const;

export const STUDIO_PLAN_SLUGS = STUDIO_PLAN_CATALOG.map((plan) => plan.slug);

type StudioPlanPricing = Pick<
  StudioPlanCatalogEntry,
  "faceMonthlyCents" | "monthlyDiscountPercent" | "annualDiscountPercent"
>;

/**
 * Catalog in force before the 2026-09-28 price change. A live subscription
 * with no price lock was bought at these, so it keeps them (grandfathered).
 */
export const LEGACY_STUDIO_PLAN_PRICES: Readonly<
  Record<StudioPlanCatalogEntry["slug"], StudioPlanPricing>
> = {
  core: { faceMonthlyCents: 15_000, monthlyDiscountPercent: 0, annualDiscountPercent: 3 },
  plus: { faceMonthlyCents: 35_000, monthlyDiscountPercent: 3, annualDiscountPercent: 8 },
  pro: { faceMonthlyCents: 100_000, monthlyDiscountPercent: 8, annualDiscountPercent: 12 },
};

/** Cancel the plan if the renewal is still unpaid after this. */
export const SUBSCRIPTION_DUNNING_MS = 7 * 24 * 60 * 60 * 1000;

export function discountedChargeCents(
  faceCents: number,
  discountPercent: number,
): number {
  const face = Math.max(0, Math.round(faceCents));
  const discount = Math.min(100, Math.max(0, Number(discountPercent) || 0));
  return Math.round((face * (100 - discount)) / 100);
}

export function creditsFromFaceCents(
  faceCents: number,
  creditPriceCents: number,
): number {
  const price = Math.max(1, Math.round(creditPriceCents));
  return Math.floor(Math.max(0, Math.round(faceCents)) / price);
}

export function discountForInterval(
  plan: Pick<StudioPlanCatalogEntry, "monthlyDiscountPercent" | "annualDiscountPercent">,
  interval: StudioPlanInterval,
): number {
  return interval === "year"
    ? plan.annualDiscountPercent
    : plan.monthlyDiscountPercent;
}

export function quoteStudioPlan(
  plan: Pick<
    StudioPlanCatalogEntry,
    "faceMonthlyCents" | "monthlyDiscountPercent" | "annualDiscountPercent"
  >,
  interval: StudioPlanInterval,
  creditPriceCents: number,
): {
  interval: StudioPlanInterval;
  discountPercent: number;
  faceMonthlyCents: number;
  chargeCents: number;
  monthlyCredits: number;
} {
  const discountPercent = discountForInterval(plan, interval);
  const faceChargeCents =
    interval === "year" ? plan.faceMonthlyCents * 12 : plan.faceMonthlyCents;
  return {
    interval,
    discountPercent,
    faceMonthlyCents: plan.faceMonthlyCents,
    chargeCents: discountedChargeCents(faceChargeCents, discountPercent),
    monthlyCredits: creditsFromFaceCents(plan.faceMonthlyCents, creditPriceCents),
  };
}

export function addCalendarMonths(ts: number, months: number): number {
  const d = new Date(ts);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0),
  ).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.getTime();
}

export function isRenewalUnpaidInvoice(payment: {
  clientRequestId?: string | null;
  reference?: string | null;
}): boolean {
  const requestId = String(payment.clientRequestId ?? "");
  const reference = String(payment.reference ?? "");
  return requestId.startsWith("sub-fail:") || /renewal unpaid/i.test(reference);
}

export function isFirstSubscribeInvoice(payment: {
  subscriptionPlanId?: unknown;
  billingInterval?: unknown;
  academyCourseId?: unknown;
  clientRequestId?: string | null;
  reference?: string | null;
}): boolean {
  if (!payment.subscriptionPlanId || !payment.billingInterval) return false;
  if (payment.academyCourseId) return false;
  if (isRenewalUnpaidInvoice(payment)) return false;
  return !String(payment.clientRequestId ?? "").startsWith("sub-paid:");
}

/**
 * Price terms stored on a subscription. Renewals, unpaid-invoice pay, Wam
 * recurring setup and monthly grants read these, never the live catalog, so
 * a catalog change only reaches new subscriptions and plan/interval switches.
 */
export type SubscriptionPriceLock = {
  lockedChargeCents?: number;
  lockedFaceMonthlyCents?: number;
  lockedMonthlyCredits?: number;
  lockedMonthlyDiscountPercent?: number;
  lockedAnnualDiscountPercent?: number;
  lockedInterval?: StudioPlanInterval;
  priceLockedAt?: number;
  priceLockSource?: "purchase" | "legacy_snapshot" | "plan_snapshot";
};

export type SubscriptionPriceTerms = {
  interval: StudioPlanInterval;
  faceMonthlyCents: number;
  monthlyDiscountPercent: number;
  annualDiscountPercent: number;
  /** Charge for one billing interval (month, or the prepaid year). */
  chargeCents: number;
  monthlyCredits: number;
  source: "locked" | "legacy" | "plan";
};

type PlanRowForPricing = {
  slug: string;
  monthlyPriceCents: number;
  originalMonthlyPriceCents?: number;
  discountPercent?: number;
  annualDiscountPercent?: number;
  includedMonthlyCredits: number;
};

export function planRowPricing(plan: PlanRowForPricing): StudioPlanPricing {
  return {
    faceMonthlyCents: plan.originalMonthlyPriceCents ?? plan.monthlyPriceCents,
    monthlyDiscountPercent: plan.discountPercent ?? 0,
    annualDiscountPercent: plan.annualDiscountPercent ?? 0,
  };
}

export function hasSubscriptionPriceLock(sub: SubscriptionPriceLock): boolean {
  return (
    typeof sub.lockedChargeCents === "number" &&
    typeof sub.lockedMonthlyCredits === "number"
  );
}

/**
 * What this subscriber pays and receives. Locked terms win; a subscription
 * with no lock predates price locks and keeps the legacy catalog.
 */
export function subscriptionPriceTerms(
  subscription: SubscriptionPriceLock & { interval?: StudioPlanInterval },
  plan: PlanRowForPricing,
  creditPriceCents: number,
): SubscriptionPriceTerms {
  const interval: StudioPlanInterval = subscription.interval ?? "month";
  if (hasSubscriptionPriceLock(subscription)) {
    const current = planRowPricing(plan);
    const pricing: StudioPlanPricing = {
      faceMonthlyCents: subscription.lockedFaceMonthlyCents ?? current.faceMonthlyCents,
      monthlyDiscountPercent:
        subscription.lockedMonthlyDiscountPercent ?? current.monthlyDiscountPercent,
      annualDiscountPercent:
        subscription.lockedAnnualDiscountPercent ?? current.annualDiscountPercent,
    };
    const sameInterval = (subscription.lockedInterval ?? interval) === interval;
    return {
      interval,
      ...pricing,
      chargeCents: sameInterval
        ? subscription.lockedChargeCents!
        : quoteStudioPlan(pricing, interval, creditPriceCents).chargeCents,
      monthlyCredits: subscription.lockedMonthlyCredits!,
      source: "locked",
    };
  }
  const legacy =
    LEGACY_STUDIO_PLAN_PRICES[plan.slug as StudioPlanCatalogEntry["slug"]];
  const pricing = legacy ?? planRowPricing(plan);
  const quote = quoteStudioPlan(pricing, interval, creditPriceCents);
  return {
    interval,
    ...pricing,
    chargeCents: quote.chargeCents,
    monthlyCredits: legacy ? quote.monthlyCredits : plan.includedMonthlyCredits,
    source: legacy ? "legacy" : "plan",
  };
}

/** Lock written when a plan or interval is bought (new, upgrade, switch). */
export function priceLockFromPurchase(args: {
  plan: PlanRowForPricing;
  interval: StudioPlanInterval;
  chargeCents: number;
  monthlyCredits: number;
  now: number;
}): Required<SubscriptionPriceLock> {
  const pricing = planRowPricing(args.plan);
  return {
    lockedChargeCents: Math.max(0, Math.round(args.chargeCents)),
    lockedFaceMonthlyCents: pricing.faceMonthlyCents,
    lockedMonthlyCredits: Math.max(0, Math.round(args.monthlyCredits)),
    lockedMonthlyDiscountPercent: pricing.monthlyDiscountPercent,
    lockedAnnualDiscountPercent: pricing.annualDiscountPercent,
    lockedInterval: args.interval,
    priceLockedAt: args.now,
    priceLockSource: "purchase",
  };
}

/** Snapshot of terms a subscriber already has (backfill; never re-prices). */
export function priceLockFromTerms(
  terms: SubscriptionPriceTerms,
  now: number,
): Required<SubscriptionPriceLock> {
  return {
    lockedChargeCents: terms.chargeCents,
    lockedFaceMonthlyCents: terms.faceMonthlyCents,
    lockedMonthlyCredits: terms.monthlyCredits,
    lockedMonthlyDiscountPercent: terms.monthlyDiscountPercent,
    lockedAnnualDiscountPercent: terms.annualDiscountPercent,
    lockedInterval: terms.interval,
    priceLockedAt: now,
    priceLockSource: terms.source === "plan" ? "plan_snapshot" : "legacy_snapshot",
  };
}
